/**
 * Description: Relationship tree component
 *
 * ------ maintenance history ------
 * 2019/8/28 By Bowen: change relationship type
 */
import { Component, OnInit, Input, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Subscription, Subject, of, Observable, forkJoin } from 'rxjs';
import { debounceTime, filter, map, catchError } from 'rxjs/operators';
import { TreeItemLookup } from '@progress/kendo-angular-treeview';
import { take } from 'rxjs/operators';
import { RelationshipTreeConfig, RelationshipTreeActions, RelationshipTreeEvents, RelationshipTreeNodeCategory, RelationshipTreeNode } from './relationship-tree.view-model';
import { ContactService } from '../../tamalelibs/services/contact.service';
import { Contact } from '../../tamalelibs/models/contact.model';
import { NoteDialogService } from '../note-dialog/note-dialog.service';
import { NoteDialogType, NoteDialogOpenOptions } from '../note-dialog/note-dialog.model';
import { EntityBrief } from '../../tamalelibs/models/entity-brief.model';
import { EntityDialogOpenOptions, EntityDialogType } from '../entity-dialog/entity-dialog.model';
import { EntityDialogService } from '../entity-dialog/entity-dialog.service';
import { SystemUser } from '../../tamalelibs/models/user.model';
import { StoreQuerierService } from '../../services/store-querier.service';
import { EntityType } from '../../tamalelibs/models/entity-type.model';
import { LONG_TOUCH_INTERVAL } from '../../tamalelibs/constants/business.constants';
import { ContextMenuService, ContextMenuComponent } from '@perfectmemory/ngx-contextmenu';
import { validRelationshipTypeSelector } from '../../redux/reducers/valid-relationship-type.reducer';
import { RelationshipService } from '../../tamalelibs/services/relationship.service';
import { ValidRelationshipType } from '../../tamalelibs/models/valid-relationship-type.model';
import { ToastService } from '../../widgets/toast/toast.service';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { RelationshipTypeActionTypes } from '../../redux/actions/relationship-type.actions';
import { AppState } from '../../redux';
import { Store, select } from '@ngrx/store';
import { RelationshipTreeService } from '../../services/relationship-tree.service';
import { RemoveRelationshipDialogService } from './remove-relationship-dialog/remove-relationship-dialog.service';
import { AddRelationshipEntityDialogService } from './add-relationship-entity-dialog/add-relationship-entity-dialog.service';
import * as _lodash from 'lodash';
import { RelationshipType } from '../../tamalelibs/models/relationship-type.model';
import { DialogAction } from '../../widgets/tam-dialog/tam-dialog.model';
import { OpenDynamicComponentService } from '../../pages/home/home-open-dynamic-component';
import { Router } from '@angular/router';
import { SlideSheetTempService } from '../../services/slide-sheet-temp.service';
import { ContactTemplateService } from '../contact-dialog-new/contact-template.service';
import { ContactTemplateOpenOptions, ContactTemplateType } from '../contact-dialog-new/contact-dialog.model';

@Component({
    selector: 'relationship-tree',
    templateUrl: './relationship-tree.component.html',
    styleUrls: ['./relationship-tree.component.scss'],
})
export class RelationshipTreeComponent implements OnInit, OnDestroy {
    @Input() config: RelationshipTreeConfig;
    @Input() eventKey?: string;
    @Input() eventValue?: string;

    @ViewChild('contextItemMenu', { static: false }) itemMenuEle: ContextMenuComponent<any>;
    @ViewChild('relationshipTypeContextMenu', { static: false }) relationshipTypeMenuEle: ContextMenuComponent<any>;
    @ViewChild('relationshipPanelContextMenu', { static: false }) relationshipPanelMenuEle: ContextMenuComponent<any>;
    totalValidRelationshipType: Array<ValidRelationshipType>;
    selectedItemValidRelationshipType = [];

    isEditDetailsShow = true;
    isExpandTreeviewNode = false;
    isChangeTypeMenuShow = false;
    isRemoveRelationshipMenuShow = false;
    isRemoveAllRelationshipMenuShow = false;
    isAddEntityMenuShow = false;
    isAddContactMenuShow = false;
    isEditTeamMemberMenuShow = false;
    isContactParent = false;

    checkableSettings = null;
    showAsShortNameChecked = true; // showAsname checked
    showAsNameChecked = false; // showAsname checked
    showNameForRemoveDialog = null;
    currentClickedItemNode = null;
    childForCurrentClickedParentNodes = null;
    // below is init function's var
    isExpanded: Function;
    children: Function;
    hasChildren: Function;

    // in order not to be affected by the kendo-tree default action, need to apply for a new variable
    // or the UI will change the config.checkedkeys directly
    relationshipTreeCheckedKeys = [];
    isClickedNode = false;

    private _checkedNode;
    private _destroySubscriptions: Array<Subscription> = [];
    private _nodeClickHandler$: Subject<any> = new Subject();
    private _currentUser: SystemUser;
    private _touchstart: number;
    private _rootNode: RelationshipTreeNode = null;

    constructor(
        private _contactTemplateService: ContactTemplateService,
        private _contactService: ContactService,
        private _noteDialogService: NoteDialogService,
        private _entityDialogService: EntityDialogService,
        private _storeQuerierService: StoreQuerierService,
        private _contextMenuService: ContextMenuService<any>,
        private _relationshipService: RelationshipService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _toast: ToastService,
        private _store: Store<AppState>,
        private _relationshipTreeService: RelationshipTreeService,
        private _router: Router,
        private _removeRelationshipDialogService: RemoveRelationshipDialogService,
        private _addRelEntityDialogService: AddRelationshipEntityDialogService,
        private _openDynamicComponentService: OpenDynamicComponentService,
        private _tempSlideSheet: SlideSheetTempService,
    ) {
    }

    ngOnInit() {
        this._currentUser = this._storeQuerierService.getCurrentUser();
        this._rootNode = this.config.nodes.find(node => node.level === '1');
        this._updateShowAsMenu(false);
        this._initRelationshipTree();
        this._destroySubscriptions.push(
            this._nodeClickHandler$.pipe(
                debounceTime(200),
                filter(event => event.originalEvent.detail === 1)
            ).subscribe(event => this._nodeClickHandler(event)),

            this._store.pipe(
                select(validRelationshipTypeSelector)
            ).subscribe(data => this.totalValidRelationshipType = data),

            this.config.actions$.subscribe(action => this._actionHandler(action)),
        );
        if (this.config.checkable) {
            this._destroySubscriptions.push(
                this._nodeClickHandler$.subscribe(event => {
                    this.isClickedNode = true;
                    this.onCheckedChange(event);
                    this.onCheckedKeysChange();
                }),
                this._relationshipTreeService.relationshipTreeOperate$.subscribe(event => {
                    if (event.type === RelationshipTreeActions.CHANGE_CHECKEDKEYS) {
                        this.relationshipTreeCheckedKeys = event.payload.concat();
                    }
                }),
            );
        }
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onCheckedChange(treeItemLookup: TreeItemLookup) {
        this._checkedNode = treeItemLookup.item.dataItem;
    }

    onCheckedKeysChange() {
        // after upgrading to angular 10, even with the same version of kendo-angular-treeview, the method of onCheckedKeysChange will be run before onCheckedChange,
        // which is different from the behavior in angular 8. so add setTimeout and wait for 200ms to change the sequence.
        setTimeout(() => {
            // in the past, the default action for kendo-treeview is that the rootNode will control all the tree node's checkedkeys, but now, the rootNode will be a seperate node, will have no relationship with other nodes
            // so, in order not to be affected by the default action in treeview, we need to define a new variables to record the original value for relatioship tree
            // and after all the operation, will give the new value to config.checkedkeys, so that it will not be affected by the default action
            // in other hand, have refactor the node click event, has merge code to one function, don't need to handle in 2 functions
            let _isTriggeredByTabChanged = false;
            if (this._checkedNode) {
                const checkedNodeUUID = this._checkedNode.uuid;
                const rootNodeCheckedKeysindex = this.relationshipTreeCheckedKeys.indexOf(this._rootNode.uuid);
                if (checkedNodeUUID === this._rootNode.uuid) {
                    // if the checked node is the rootNode
                    if (rootNodeCheckedKeysindex > -1) {
                        this.relationshipTreeCheckedKeys.splice(rootNodeCheckedKeysindex, 1);
                    } else {
                        this.relationshipTreeCheckedKeys.push(checkedNodeUUID);
                    }
                } else {
                    // if the checked node is not the rootNode
                    const tempIndex = this.relationshipTreeCheckedKeys.findIndex(item => item === checkedNodeUUID);
                    if (this._checkedNode && this._checkedNode.isLeaf) {
                        // if the node is a leaf node, we need to handle 2 situations:
                        // one is from click the node
                        if (this.isClickedNode) {
                            if (tempIndex > -1) {
                                this.relationshipTreeCheckedKeys.splice(tempIndex, 1);
                            } else {
                                this.relationshipTreeCheckedKeys.push(checkedNodeUUID);
                            }
                            const relationshipConfig = {
                                nodes: this.config.nodes,
                                checkedKeys: this.relationshipTreeCheckedKeys
                            };
                            this.relationshipTreeCheckedKeys = this._relationshipTreeService.updateCheckedKeysByLeafNode(this._checkedNode, relationshipConfig);
                        } else {
                            if (this.isExpandTreeviewNode) { // to handle the expand node case
                                this.config.checkedKeys = this.relationshipTreeCheckedKeys.concat();
                                this.isExpandTreeviewNode = false;
                            } else {
                                // one is from click the checkbox
                                // use the default value, but need to handle the rootNode
                                this.relationshipTreeCheckedKeys = this.config.checkedKeys.concat();
                                if (rootNodeCheckedKeysindex > -1) {
                                    this.relationshipTreeCheckedKeys.push(this._rootNode.uuid);
                                } else {
                                    // the root node didn't be selected in the past
                                    // we need to remove if it is selected now
                                    const index = this.relationshipTreeCheckedKeys.indexOf(this._rootNode.uuid);
                                    if (index > -1) {
                                        this.relationshipTreeCheckedKeys.splice(index, 1);
                                    }
                                }
                            }
                        }
                    } else {
                        const affectedIds: Array<string> = this.config.nodes.filter(node => node.uuid.indexOf(checkedNodeUUID) > -1).map(node => node.uuid);
                        if (tempIndex > -1) {
                            this.relationshipTreeCheckedKeys.splice(tempIndex, 1);
                            affectedIds.forEach(id => {
                                const oneIndex = this.relationshipTreeCheckedKeys.findIndex(item => item === id);
                                if (oneIndex > -1) {
                                    this.relationshipTreeCheckedKeys.splice(oneIndex, 1);
                                }
                            });
                        } else {
                            this.relationshipTreeCheckedKeys.push(checkedNodeUUID);
                            affectedIds.forEach(id => {
                                const oneIndex = this.relationshipTreeCheckedKeys.findIndex(item => item === id);
                                if (oneIndex < 0) {
                                    this.relationshipTreeCheckedKeys.push(id);
                                }
                            });
                        }
                    }
                }
            } else {
                _isTriggeredByTabChanged = true;
            }
            this.config.checkedKeys = this.relationshipTreeCheckedKeys.concat();
            this.config.events$.next({
                type: RelationshipTreeEvents.CHECKED_NODE_CHANGED,
                payload: {
                    isTriggeredByTabChanged: _isTriggeredByTabChanged,
                    chekedKeys: this.config.checkedKeys
                }
            });
            this.isClickedNode = false;
            this._changeDetectorRef.detectChanges();
        }, 200);
    }

    ontouchstart(event) {
        this._touchstart = new Date().getTime();
    }

    ontouchend(event, dataItem) {
        if (new Date().getTime() - this._touchstart > LONG_TOUCH_INTERVAL) {
            event.clientX = event.pageX;
            event.clientY = event.pageY;
            this.onContextMenu(event, dataItem);
            this.mouseRightClick(event, dataItem);
        }
    }

    onContextMenu(event, dataItem) {
        if (dataItem && dataItem.nodeType === RelationshipTreeNodeCategory.ENTITY) {
            // add conditional for relationship team edit.
            if (!this._currentUser.credential.manageTeam && dataItem && dataItem.type && dataItem.type.id === EntityType.TEAM.id) {
                this.isEditDetailsShow = false;
            } else {
                this.isEditDetailsShow = true;
            }
        }
    }

    onNodeClick(event) {
        switch (event.type) {
            case 'click':
                // use rxjs stream to identify the single/double click
                // if no other click in 200ms, will treat it as single click.
                this._nodeClickHandler$.next(event);
                break;
            case 'contextmenu':
                // right click to trigger context menu
                if (event.item && event.item.dataItem && event.item.dataItem.nodeType === RelationshipTreeNodeCategory.ENTITY) {
                    // add conditional for relationship team edit.
                    if (!this._currentUser.credential.manageTeam && event.item.dataItem && event.item.dataItem.type &&
                        event.item.dataItem.type.id === EntityType.TEAM.id) {
                        this.isEditDetailsShow = false;
                    } else {
                        this.isEditDetailsShow = true;
                    }
                }
                break;
            default:
                break;
        }
    }

    onNodeDblClick(event) {
        if (event.item.dataItem.nodeType === RelationshipTreeNodeCategory.ENTITY && event.item.dataItem.level !== '1') {
            // only trigger double click on leaf node
            this.config.events$.next({
                type: RelationshipTreeEvents.NODE_DOUBLE_CLICKED,
                payload: event.item.dataItem
            });
            this._tempSlideSheet.actionSubject$.next('');
            const node = event.item.dataItem;
            const routeType = node.type && node.type.name === 'Contact' ? 'contact' : 'entity';
            this._router.navigate([routeType, node.id]);
        }
    }

    changeRelationshipType(event, item) {
        const createRelationshipObj = {};
        createRelationshipObj['appendRelationship'] = true;
        createRelationshipObj['relTypeId'] = item.id;
        createRelationshipObj['currentControlValues'] = [this.currentClickedItemNode.id];
        createRelationshipObj['dependenceControlValues'] = [this._rootNode.id];

        this._relationshipService.createRelationship(createRelationshipObj).pipe(
            catchError((err) => {
                this._toastMessageHandler('Relationship updated failed.', NotificationStyles.Error);
                this._store.dispatch({
                    type: RelationshipTypeActionTypes.GETVALIDRELATIONSHIP
                });
                return of(null);
            }),
            take(1)
        ).subscribe((res) => {
            if (res) {
                this._relationshipService.deleteRelationship(this.currentClickedItemNode.relationshipId, true).pipe(
                    catchError((err) => {
                        if (err.message && err.message.indexOf('404') > -1 && err.message.indexOf('ERR_NOT_FOUND') > -1) {
                            this._store.dispatch({
                                type: RelationshipTypeActionTypes.GETVALIDRELATIONSHIP
                            });
                            return of('ERR_NOT_FOUND');
                        } else {
                            this._relationshipService.deleteRelationship(res.id, true);
                            this._toastMessageHandler('Relationship updated failed.', NotificationStyles.Error);
                            return of('ERROR');
                        }
                    }),
                    take(1)
                ).subscribe((response) => {
                    this._changeDetectorRef.detectChanges();
                    if (response !== 'ERROR') {
                        // need to seperate, or all the screens will refresh
                        this._relationshipTreeService.relationshipTreeOperate$.next({
                            type: RelationshipTreeActions.CHANGE_RELATIONSHIP,
                            payload: {
                                rootNodeId: this._rootNode.id,
                                clickedItem: this.currentClickedItemNode,
                                targetRelationshipType: item
                            }
                        });
                        this._toastMessageHandler('Relationship successfully updated.', NotificationStyles.Success);
                    }
                });
            }
        });
    }

    mouseRightClickOnPanel(event) {
        if (this._currentUser.credential.editRelationshipTree) {
            this.isAddEntityMenuShow = true;
            this.isAddContactMenuShow = true;
        } else {
            this.isAddEntityMenuShow = false;
            this.isAddContactMenuShow = false;
        }
        if (this._rootNode.type.id === EntityType.TEAM.id && this._currentUser.credential.manageTeam) {
            this.isEditTeamMemberMenuShow = true;
        } else {
            this.isEditTeamMemberMenuShow = false;
        }
        this._changeDetectorRef.detectChanges();
        this._contextMenuService.show(this.relationshipPanelMenuEle, { x: event.clientX, y: event.clientY });
        event.preventDefault();
        event.stopPropagation();
    }

    mouseRightClick(event, item) {
        this.currentClickedItemNode = item;
        this.onContextMenu(event, item);
        // if have the edit relationship crediential, user will be able to remove, add contact/entity, should not affect edit team member
        // ppc user will be able to edit team member
        this.isChangeTypeMenuShow = false;
        this.isRemoveRelationshipMenuShow = false;
        this.isAddEntityMenuShow = false;
        this.isEditTeamMemberMenuShow = false;
        const selfRelationshipArray: Array<any> = [];
        const entityCondition = item.parentId !== RelationshipTreeNodeCategory.TEAMS_GROUP && item.parentId !== RelationshipTreeNodeCategory.TEAM_MEMBER_GROUP;
        const currentEmploymentControl = item.parentId && (!(item.parentId.includes(RelationshipTreeNodeCategory.FORMER_EMPLOYMENT_GROUP)
            || item.parentId.includes(RelationshipTreeNodeCategory.CURRENT_EMPLOYMENT_GROUP)));

        // this function is for add contact
        // contact cannot add contact, entity and teams can add contact
        // entity can add contact and entity
        // teams can add contact and entity
        if (this._currentUser.credential.editRelationshipTree) {
            this.isAddContactMenuShow = true;
        } else {
            this.isAddContactMenuShow = false;
        }

        // this condition is for add entity and remove relationship
        // to make sure if the remove function needs to show in menu
        if (entityCondition && this._currentUser.credential.editRelationshipTree) {
            this.isAddEntityMenuShow = true;
            if (currentEmploymentControl) {
                this.isRemoveRelationshipMenuShow = true;
            } else {
                this.isRemoveRelationshipMenuShow = false;
            }
        } else {
            this.isRemoveRelationshipMenuShow = false;
            this.isAddEntityMenuShow = false;
        }

        // get self relationship type array
        if (item.level !== '1') {
            this.config.nodes.forEach(element => {
                if (item.id === element.id) {
                    selfRelationshipArray.push(element.parentId.slice(-32));
                }
            });
        }
        // for root node context menu
        if (item.level === '1') {
            if (this._currentUser.credential.editRelationshipTree) {
                this.isAddEntityMenuShow = true;
                this.isRemoveRelationshipMenuShow = false;
            }
            // this condition is for edit team member function
            // only rootNode is a team, the context menu will show edit team member
            if (this._rootNode.type.id === EntityType.TEAM.id && this._currentUser.credential.manageTeam) {
                this.isEditTeamMemberMenuShow = true;
            } else {
                this.isEditTeamMemberMenuShow = false;
            }
            this._changeDetectorRef.detectChanges();
            this._contextMenuService.show(this.itemMenuEle, { x: event.clientX, y: event.clientY });
        }

        // for relationship type and team member group and teams group
        if (item.level === '3') {
            // edit team member
            if (this._rootNode.type.id === EntityType.TEAM.id && item.parentId === RelationshipTreeNodeCategory.TEAM_MEMBER_GROUP &&
                this._currentUser.credential.manageTeam) {
                this.isEditTeamMemberMenuShow = true;
            } else {
                this.isEditTeamMemberMenuShow = false;
            }

            // control remove relationship show
            if (currentEmploymentControl) {
                this.isRemoveAllRelationshipMenuShow = true;
            } else {
                this.isRemoveAllRelationshipMenuShow = false;
            }

            // only contact relationship type can add contact
            if (entityCondition && this._currentUser.credential.editRelationshipTree) {
                this.isAddEntityMenuShow = true;
                this._changeDetectorRef.detectChanges();
                this._contextMenuService.show(this.relationshipTypeMenuEle, { x: event.clientX, y: event.clientY });
            } else {
                this.isAddEntityMenuShow = false;
                this._changeDetectorRef.detectChanges();
                this._contextMenuService.show(this.itemMenuEle, { x: event.clientX, y: event.clientY });
            }
        }

        // for entity and contact node
        if (item.id && item.level === '4') {
            if (this._currentUser.credential.editRelationshipTree) {
                this.selectedItemValidRelationshipType.splice(0, this.selectedItemValidRelationshipType.length);
                this.totalValidRelationshipType.forEach(element => {
                    // common condition
                    const commonCondition = element.childEntityType.id === item.type.id &&
                        element.parentEntityType.id === this._rootNode.type.id;
                    // contact all condition
                    const contactAllCondition = element.childEntityType.id === EntityType.CONTACT.id &&
                        element.parentEntityType.id === EntityType.CONTACT.id;
                    // teams condition
                    const selfCondition = selfRelationshipArray && selfRelationshipArray.includes(element.relationshipType.id);

                    // to make sure if the remove function needs to show in menu
                    if (entityCondition) {
                        if (currentEmploymentControl) {
                            this.isRemoveRelationshipMenuShow = true;
                        } else {
                            this.isRemoveRelationshipMenuShow = false;
                        }
                        this.isAddEntityMenuShow = true;
                    } else {
                        this.isRemoveRelationshipMenuShow = false;
                        this.isAddEntityMenuShow = false;
                    }

                    // get the selectedItemValidRelationshipType for change type function
                    if (this._rootNode.type.id === EntityType.CONTACT.id) {
                        if (item.type.id === EntityType.CONTACT.id && !selfCondition) {
                            // parent: contact child: entity
                            if (element.childEntityType.id === EntityType.CONTACTSFORVALIDRELTYPE.id &&
                                element.parentEntityType.id === EntityType.CONTACTSFORVALIDRELTYPE.id) {
                                this.selectedItemValidRelationshipType.push({
                                    'id': element.relationshipType.id,
                                    'caption': element.relationshipType.name,
                                    'items': null
                                });
                            }
                        } else {
                            // parent: contact child: contact
                            if ((commonCondition || contactAllCondition) && entityCondition && !selfCondition &&
                                element.childEntityType.id !== EntityType.CONTACTSFORVALIDRELTYPE.id &&
                                element.parentEntityType.id !== EntityType.CONTACTSFORVALIDRELTYPE.id) {
                                this.selectedItemValidRelationshipType.push({
                                    'id': element.relationshipType.id,
                                    'caption': element.relationshipType.name,
                                    'items': null
                                });
                            }
                        }
                    } else {
                        if ((item.type.id === EntityType.CONTACT.id) &&
                            (commonCondition || contactAllCondition) && entityCondition && !selfCondition) {
                            // parent: entity child: contact
                            this.selectedItemValidRelationshipType.push({
                                'id': element.relationshipType.id,
                                'caption': element.relationshipType.name,
                                'items': null
                            });
                        } else if (commonCondition && !selfCondition) {
                            // parent: entity child: entity
                            this.selectedItemValidRelationshipType.push({
                                'id': element.relationshipType.id,
                                'caption': element.relationshipType.name,
                                'items': null
                            });
                        }
                    }
                });
                if (this.selectedItemValidRelationshipType.length !== 0 && currentEmploymentControl) {
                    this.isChangeTypeMenuShow = true;
                    this.selectedItemValidRelationshipType = _lodash.uniqWith(
                        this.selectedItemValidRelationshipType, _lodash.isEqual);
                    this.selectedItemValidRelationshipType = _lodash.sortBy(this.selectedItemValidRelationshipType, element => element.caption);
                }
            }
            this._changeDetectorRef.detectChanges();
            this._contextMenuService.show(this.itemMenuEle, { x: event.clientX, y: event.clientY });
        }
        event.preventDefault();
        event.stopPropagation();
    }

    onMenuSelect(event) {
        if (event) {
            const type = event.event.target.innerText; // context menu type id
            let item = this.currentClickedItemNode;
            if (!item) {
                item = this._rootNode;
            }
            switch (type) {
                case 'View Research':
                    this.config.events$.next({
                        type: RelationshipTreeEvents.CONTEXTMENU_VIEWRESEARCH,
                        payload: item
                    });
                    break;
                case 'View Details':
                    if (item.type.id === EntityType.CONTACT.id) {
                        this._router.navigate(['contact', item.id]);
                    } else {
                        this._router.navigate(['entity', item.id]);
                    }
                    break;
                case 'Edit Details':
                    this._openEditDetailDialog(item);
                    break;
                case 'New Note':
                    this._openNoteDialog(item);
                    break;
                case 'Edit Team Members':
                    this._openEditTeamMemberDialog(this._rootNode);
                    break;
                case 'Remove Relationship':
                    this._openRemoveItemRelationshipDialog(item);
                    break;
                case 'Add Entity':
                    this._openAddEntityDialog(RelationshipTreeActions.ADD_ENTITIES, item);
                    break;
                case 'Add Contact':
                    this._openAddEntityDialog(RelationshipTreeActions.ADD_CONTACTS, item);
                    break;
                case 'Show as Shortname':
                    this._triggerShowAsChange('shortName');
                    break;
                case 'Show as Name':
                    this._triggerShowAsChange('name');
                    break;
                case 'Remove All of This Type':
                    this._openRemoveParentRelationshipDialog(item);
                    break;
                default:
                    break;
            }

            this._changeDetectorRef.detectChanges();
        }
    }

    trackByFn(index, item) {
        return item.id;
    }

    // here is to handle relationship tree expandkeys
    handleCollapse(node) {
        this.config.expandedKeys = this.config.expandedKeys.filter(item => item !== node.dataItem.uuid);
    }

    handleExpand(node) {
        this.isExpandTreeviewNode = true;
        this.config.expandedKeys = this.config.expandedKeys.concat(node.dataItem.uuid);

    }

    onClick(event) {
        this._decorateEvent(event);
    }

    // keep slide sheet open
    private _decorateEvent(event) {
        if (this.eventKey) {
            event[this.eventKey] = this.eventValue;
        }
    }

    private _initRelationshipTree() {
        this.relationshipTreeCheckedKeys = this.config.checkedKeys.concat();
        // make sure if it has checkbox or expandkey
        if (this.config.expandable) {
            this.isExpanded = (dataItem: any, index: string) => {
                return this.config.expandedKeys.findIndex(item => item === dataItem.uuid) > -1;
            };
            // children: this function will provide the child nodes
            this.children = (node: any): Observable<any[]> => {
                const expandedKeysChildren = [];
                this.config.nodes.forEach(item => {
                    if (item.parentId === node.dataItem.uuid) {
                        expandedKeysChildren.push(item.uuid);
                    }
                });
                return of(expandedKeysChildren);
            };
            this.hasChildren = (node: any): boolean => {
                const expandedKeysChildren = [];
                this.config.nodes.forEach(item => {
                    if (item.parentId === node.dataItem.uuid) {
                        expandedKeysChildren.push(item.uuid);
                    }
                });
                if (expandedKeysChildren.length === 0) {
                    return false;
                } else {
                    return true;
                }
            };
        } else {
            this.isExpanded = null;
            this.children = null;
            this.hasChildren = null;
        }
        // init checkbox settings
        this.checkableSettings = {
            checkChildren: true,
            checkParents: true,
            enabled: this.config.checkable,
            mode: 'multiple',
        };
        this._changeDetectorRef.detectChanges();
    }

    private _actionHandler(action) {
        if (action.type === RelationshipTreeActions.RESET) {
            this._updateShowAsMenu();
        }
    }

    private _toastMessageHandler(message, toastStyle: NotificationStyles) {
        this._toast.notify({
            message: message,
            style: toastStyle
        });
    }

    private _successRemoveHandler(rootNode, item) {
        // in order to improve performance, no need to reload all relationship
        this._relationshipTreeService.relationshipTreeOperate$.next({
            type: RelationshipTreeActions.REMOVE_RELATIONSHIP,
            payload: {
                rootNodeId: rootNode.id,
                removeItem: item
            }
        });
        this._toastMessageHandler('Relationship successfully deleted.', NotificationStyles.Success);
        // to avoid error when right click at blank area and click 'add contact/entity'
        this.currentClickedItemNode = this._rootNode;
    }

    private _updateShowAsMenu(detect = true) {
        if (this.config.showAs === 'name') {
            this.showAsShortNameChecked = false;
            this.showAsNameChecked = true;
        } else {
            this.showAsShortNameChecked = true;
            this.showAsNameChecked = false;
        }
        if (detect) {
            this._changeDetectorRef.detectChanges();
        }
    }

    private _nodeClickHandler(event) {
        // get the checkbox dom and programically check it to trigger TreeView behavior
        const checkboxDom = event.originalEvent.target.parentNode.querySelector('.k-checkbox');
        if (checkboxDom) {
            // need to make sure we cannot click the word, should click the checkbox, or it will alert error:
            // Cannot read property 'click' of null
            // TPM has changed this business logic, so need to remove this line
            // checkboxDom.click();
        } else {
            this.config.events$.next({
                type: RelationshipTreeEvents.NODE_SINGLE_CLICKED,
                payload: event.item.dataItem
            });
        }
    }

    // open edit detail dialog
    private _openEditDetailDialog(item) {
        if (item.type.id === EntityType.CONTACT.id) {
            this._openContactDialog(item.id);
        } else {
            // add 'edit entity'
            const entityDialogOpenOptions = new EntityDialogOpenOptions();
            entityDialogOpenOptions.openType = EntityDialogType.EditEntity;
            entityDialogOpenOptions.entityId = item.id;
            entityDialogOpenOptions.eventKey = this.eventKey;
            entityDialogOpenOptions.eventValue = this.eventValue;
            this._entityDialogService.dialogOpen$.next(entityDialogOpenOptions);
        }
        this._openDynamicComponentService.feedbackSubject$.pipe(
            take(1)
        ).subscribe(res => {
            if (res.type === DialogAction.CloseDialog && res.payload) {
                this._relationshipTreeService.relationshipTreeOperate$.next({
                    type: RelationshipTreeActions.EDIT_DETAILS,
                    payload: {
                        itemId: item.id,
                    }
                });
            }
        });
    }

    // open edit team member dialog
    // after edit, no refresh, because cannot keep checkedkeys
    private _openEditTeamMemberDialog(item) {
        const entityDialogOpenOptions = new EntityDialogOpenOptions();
        entityDialogOpenOptions.openType = EntityDialogType.EditEntity;
        entityDialogOpenOptions.entityId = item.id;
        entityDialogOpenOptions.openTab = 1;
        entityDialogOpenOptions.eventKey = this.eventKey;
        entityDialogOpenOptions.eventValue = this.eventValue;
        this._entityDialogService.dialogOpen$.next(entityDialogOpenOptions);
    }

    // open add entity to relationship tree's dialog
    private _openAddEntityDialog(title, item) {
        // request:
        // 1. when click on relationship panel, all the context menu need to show
        // 2. when click on entity item, need to show the dialog
        // 3. when click on relationship type, this type need to show in filter panel as default
        // 4. we can choose to create reciprocal relationship and replace the origin entities below this relationship type
        // 5. support user to create the non-existing entity
        let currentRelationshipType: RelationshipType;
        const dialogCheckboxConfig = {
            isReplaceWithCreatedEntity: false, // replace
            isCreatWithReciprocalRel: true,  // create reciprocal
            isCreatWithReciprocalRelShow: false,  // show the create reciprocal checkbox
            isReplaceExistingShow: true // show replace checkbox
        };
        // here is the new request: if we click the rel-type, add contact and add entity need to show at the same time
        // but only valid operation will be added to dialog defaultly
        let operation = '';
        if (item.parentId === RelationshipTreeNodeCategory.ENTITY_GROUP) {
            operation = RelationshipTreeActions.ADD_ENTITIES;
        } else if (item.parentId === RelationshipTreeNodeCategory.CONTACTS_GROUP) {
            operation = RelationshipTreeActions.ADD_CONTACTS;
        }
        if (item.level === '3' && item.parentId !== RelationshipTreeNodeCategory.TEAM_MEMBER_GROUP && item.parentId !== RelationshipTreeNodeCategory.TEAMS_GROUP
            && operation === title) {
            currentRelationshipType = new RelationshipType(item.uuid.slice(-32), item.name);
        } else if (item.level === '4') {
            // if click the entity/contact node, the rel-type should show in the filter dialog
            if (item.parentId && ((item.parentId.includes(RelationshipTreeNodeCategory.ENTITY_GROUP) && title === RelationshipTreeActions.ADD_ENTITIES) ||
                (item.parentId.includes(RelationshipTreeNodeCategory.CONTACTS_GROUP) && title === RelationshipTreeActions.ADD_CONTACTS))) {
                const name = this.config.nodes.filter(element => item.parentId === element.uuid)[0].name;
                currentRelationshipType = new RelationshipType(item.parentId.slice(-32), name);
            }
        }

        // get dialog title
        let dialogTitle = '';
        if (this._rootNode.name === this._rootNode.shortName) {
            dialogTitle = title + ' to ' + this._rootNode.name;
        } else {
            dialogTitle = title + ' to ' + this._rootNode.name + ' (' + this._rootNode.shortName + ')';
        }


        this.selectedItemValidRelationshipType = [];
        // here is to filter valid relationship for clicked entity
        this.totalValidRelationshipType.forEach(element => {
            if (title === RelationshipTreeActions.ADD_ENTITIES) {
                // when clicked item is an entity
                if (this._rootNode.type.id !== EntityType.CONTACT.id) {
                    if (element.childEntityType.id !== EntityType.CONTACT.id && element.childEntityType.id !== EntityType.TEAM.id
                        && element.childEntityType.id !== EntityType.USER.id && this._rootNode.type.id === element.parentEntityType.id) {
                        this.selectedItemValidRelationshipType.push({
                            'id': element.relationshipType.id,
                            'caption': element.relationshipType.name,
                        });
                    }
                } else {
                    if (element.childEntityType.id === EntityType.CONTACT.id &&
                        element.parentEntityType.id === EntityType.CONTACT.id) {
                        this.selectedItemValidRelationshipType.push({
                            'id': element.relationshipType.id,
                            'caption': element.relationshipType.name,
                        });
                    }
                }
            } else {
                // Add Contacts
                if (this._rootNode.type.id !== EntityType.CONTACT.id) {
                    if (element.childEntityType.id === EntityType.CONTACT.id &&
                        element.parentEntityType.id === EntityType.CONTACT.id) {
                        this.selectedItemValidRelationshipType.push({
                            'id': element.relationshipType.id,
                            'caption': element.relationshipType.name,
                        });
                    }
                } else {
                    if (element.childEntityType.id === EntityType.CONTACTSFORVALIDRELTYPE.id &&
                        element.parentEntityType.id === EntityType.CONTACTSFORVALIDRELTYPE.id) {
                        this.selectedItemValidRelationshipType.push({
                            'id': element.relationshipType.id,
                            'caption': element.relationshipType.name,
                        });
                    }
                }
            }
        });

        if (title === RelationshipTreeActions.ADD_CONTACTS) {
            dialogCheckboxConfig.isCreatWithReciprocalRelShow = false;
        } else {
            if (this._rootNode.type.id === EntityType.CONTACT.id) {
                dialogCheckboxConfig.isCreatWithReciprocalRelShow = false;
            } else {
                dialogCheckboxConfig.isCreatWithReciprocalRelShow = true;
            }
        }

        // to remove the same item and sort
        if (this.selectedItemValidRelationshipType.length !== 0) {
            this.selectedItemValidRelationshipType = _lodash.uniqWith(
                this.selectedItemValidRelationshipType, _lodash.isEqual);
            this.selectedItemValidRelationshipType = _lodash.sortBy(this.selectedItemValidRelationshipType, element => element.caption);
        }

        // send the data to subject and will operate
        this._addRelEntityDialogService.openAddEntityDialog(dialogTitle, title, currentRelationshipType, this._rootNode,
            this.selectedItemValidRelationshipType, this.totalValidRelationshipType, dialogCheckboxConfig, this.eventKey, this.eventValue)
            .pipe(
                catchError((err) => {
                    this._toastMessageHandler(err.message, NotificationStyles.Error);
                    return of(null);
                }),
                take(1)
            ).subscribe(res => {
                if (res && res.type === RelationshipTreeEvents.ADD_ENTITY) {
                    // to boradcast the action to entity screen and contact screen and research screen
                    this._relationshipTreeService.relationshipTreeOperate$.next({
                        type: RelationshipTreeActions.ADD_RELATIONSHIP,
                        payload: {
                            rootNodeId: this._rootNode.id,
                            relationshipTypes: res.payload.relationshipTypes,
                        }
                    });

                    // to toast message for success and error status
                    if (res.payload.serverResponse === 'error') {
                        if (title === RelationshipTreeActions.ADD_ENTITIES) {
                            this._toastMessageHandler('Adding entity(s) failed, please try again.', NotificationStyles.Error);
                        } else {
                            this._toastMessageHandler('Adding contact(s) failed, please try again.', NotificationStyles.Error);
                        }
                    } else {
                        if (title === RelationshipTreeActions.ADD_ENTITIES) {
                            this._toastMessageHandler('All entities are successfully added.', NotificationStyles.Success);
                        } else {
                            this._toastMessageHandler('All contacts are successfully added.', NotificationStyles.Success);
                        }
                    }
                }
            });
    }

    // open contact dialog
    private _openContactDialog(id) {
        this._contactService.getContactDetail(id).pipe(
            take(1),
            map(response => {
                return Contact.parse(response, true);
            })).subscribe(response => {
                const _contactDialogOpenOptions = new ContactTemplateOpenOptions();
                _contactDialogOpenOptions.openType = ContactTemplateType.EditContact;
                _contactDialogOpenOptions.contact = response;
                _contactDialogOpenOptions.eventKey = this.eventKey;
                _contactDialogOpenOptions.eventValue = this.eventValue;
                this._contactTemplateService.dialogOpen$.next(_contactDialogOpenOptions);
            });
    }

    // delete relationship
    // var checkedkey is to reduce the foreach operation in research screen
    private _removeRelationship(action, item) {
        // delete one relationship
        if (action.type === RelationshipTreeEvents.REMOVE_ONE_RELATIONSHIP) {
            this._relationshipService.deleteRelationship(
                this.currentClickedItemNode.relationshipId, action.payload.isDeleteReciprocalRel).
                pipe(
                    catchError(() => {
                        this._toastMessageHandler('Relationship deleted failed.', NotificationStyles.Error);
                        return of('ERROR');
                    }),
                    take(1)
                ).subscribe((response) => {
                    if (response !== 'ERROR') {
                        this._successRemoveHandler(this._rootNode, item);
                    }
                });
        }

        // delete multi relationship
        if (action.type === RelationshipTreeEvents.REMOVE_MULTI_RELATIONSHIP) {
            const deleteResponse: Array<Observable<any>> = [];
            // childForCurrentClickedParentNodes includes: children node and relationshiptype node
            const parentNodeLength = this.childForCurrentClickedParentNodes.length - 1;
            for (let i = 0; i < parentNodeLength; i++) {
                deleteResponse.push(
                    this._relationshipService.deleteRelationship(
                        this.childForCurrentClickedParentNodes[i].relationshipId, action.payload.isDeleteReciprocalRel).pipe(
                            catchError(() => {
                                this._toastMessageHandler('Relationship deleted failed.', NotificationStyles.Error);
                                return of('ERROR');
                            }),
                            take(1),
                        ),
                );
            }
            forkJoin(deleteResponse).subscribe(res => {
                res.forEach(response => {
                    if (response === 'ERROR') {
                        this._toastMessageHandler('Relationship deleted failed.', NotificationStyles.Error);
                    }
                });
                this._successRemoveHandler(this._rootNode, item);
            });
        }
    }

    private _openRemoveItemRelationshipDialog(item) {
        this.currentClickedItemNode = item;
        // 1: get rootNode information
        const rootNodeName = this._rootNode.shortName;
        let isRootNodeForEntity = true;
        if (this._rootNode.type.id === EntityType.CONTACT.id) {
            isRootNodeForEntity = false;
        }

        // 2: get relationship type information
        let relationshipTypeName;
        for (let i = 0; i < this.config.nodes.indexOf(item); i++) {
            if (item.parentId === this.config.nodes[i].uuid) {
                relationshipTypeName = this.config.nodes[i].name;
            }
        }

        // 3: get clicked item name: need to judge according to showAsName
        if (this.showAsShortNameChecked) {
            this.showNameForRemoveDialog = item.shortName;
        } else {
            this.showNameForRemoveDialog = item.name;
        }

        // 4: get the var: isContactParent
        const itemSelfType = item.type.name;
        if (itemSelfType === 'Contact') {
            this.isContactParent = true;
        } else {
            this.isContactParent = false;
        }

        // 5: send the data to subject
        this._removeRelationshipDialogService.open(
            this._rootNode, rootNodeName, relationshipTypeName, this.showNameForRemoveDialog, this.isContactParent, isRootNodeForEntity, this.eventKey, this.eventValue).pipe(
                take(1),
            ).
            subscribe(data => {
                this._removeRelationship(data, item);
            });
    }

    private _openRemoveParentRelationshipDialog(item) {
        // 1: get rootNode information
        const rootNodeName = this._rootNode.shortName;
        let isRootNodeForEntity = true;
        if (this._rootNode.type.name === 'Contact') {
            isRootNodeForEntity = false;
        }

        // 2: get relationship type information
        const allNodesLength = this.config.nodes.length;
        const newArr = [];
        for (let i = 0; i < allNodesLength; i++) {
            if (item.uuid === this.config.nodes[i].parentId) {
                newArr.push(this.config.nodes[i]);
            }
        }
        this.childForCurrentClickedParentNodes = newArr;
        newArr.push(item);

        // 3: get the var: isContactParent
        const itemSelfType = item.parentId;
        if (itemSelfType === RelationshipTreeNodeCategory.CONTACTS_GROUP) {
            this.isContactParent = true;
        } else {
            this.isContactParent = false;
        }
        // 4: send the data to subject
        this._removeRelationshipDialogService.open(
            this._rootNode, rootNodeName, item.name, null, this.isContactParent, isRootNodeForEntity, this.eventKey, this.eventValue).pipe(
                take(1),
            ).subscribe(data => {
                this._removeRelationship(data, item);
            });
    }

    // open note dialog
    private _openNoteDialog(item) {
        const entity: EntityBrief = {
            id: item.id,
            name: item.name,
            shortName: item.shortName,
            type: item.type,
            isPublic: item.isPublic,
            company: item.company,
        };
        const noteParam: NoteDialogOpenOptions = {
            openType: NoteDialogType.CreateNote,
            isPlugInEditNoteMode: false,
            entity: entity,
            minimizable: false,
            entryId: '',
            entityFieldEditable: false,
            eventKey: this.eventKey,
            eventValue: this.eventValue
        };
        this._noteDialogService.dialogOpen$.next(noteParam);
    }

    private _triggerShowAsChange(showAs) {
        this.config.showAs = showAs;
        this._updateShowAsMenu();
        this.config.events$.next({
            type: RelationshipTreeEvents.CONTEXTMENU_SHOWNAMEFORMAT,
            payload: showAs
        });
    }

    // TODO: Didn't use; keep it for later enhancement; need to refine the algorithm
    // private _calculateExpectedCheckedKeys(curCheckedKeys: string[], treeNodes: RelationshipTreeNode[]): string[] {
    //     // let tempCheckedKeys = Object.assign([], curCheckedKeys);
    //     const tempCheckedKeys = new Set(curCheckedKeys);
    //     const rootNode = this.config.nodes.find(node => node.level === '1');
    //     this._checkAllChildrens(tempCheckedKeys, treeNodes, rootNode.uuid);
    //     this._changeDetectorRef.detectChanges();
    //     return Array.from(tempCheckedKeys);
    // }

    // TODO: Didn't use; keep it for later enhancement; need to refine the algorithm
    // private _checkAllChildrens(checkedKeys: Set<string>, treeNodes: RelationshipTreeNode[], currentNodeId: string) {
    //     const currentNode = treeNodes.filter(node => node.uuid === currentNodeId)[0];
    //     if (currentNode && currentNode.isLeaf) {
    //         if (checkedKeys && checkedKeys.has(currentNode.parentId)) {
    //             return;
    //         }
    //         // logic to decide whether all sibling nodes (include itself) was checked -> check parent node
    //         const siblingNodes = treeNodes.filter(node => node.parentId === currentNode.parentId);
    //         let allChecked = true;
    //         for (let index = 0; index < siblingNodes.length; index++) {
    //             if (checkedKeys && !checkedKeys.has(siblingNodes[index].uuid)) {
    //                 allChecked = false;
    //                 break;
    //             }
    //         }
    //         if (allChecked) {
    //             checkedKeys.add(currentNode.parentId);
    //         }
    //     } else {
    //         const childNodes = treeNodes.filter(node => node.parentId === currentNodeId);
    //         if (currentNode && currentNode.uuid && checkedKeys && checkedKeys.has(currentNode.uuid)) {
    //             // current node is checked
    //             childNodes.forEach(element => {
    //                 if (!checkedKeys.has(element.uuid)) {
    //                     checkedKeys.add(element.uuid);
    //                 }
    //             });
    //         }
    //         if (childNodes && childNodes.length > 0) {
    //             childNodes.forEach(element => {
    //                 this._checkAllChildrens(checkedKeys, treeNodes, element.uuid);
    //             });
    //         }
    //     }
    //     this._changeDetectorRef.detectChanges();
    // }
}
